import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Field } from '../components/Field';
import { useAuth } from '../lib/auth';
import { useSEO } from '../hooks/useSEO';

type Mode = 'signin' | 'signup' | 'reset';

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, signUp, resetPassword, user } = useAuth();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // After login, redirect to the page the user was trying to access (or /mon-espace).
  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/mon-espace';

  useSEO({
    title: 'Espace membre — MTC',
    description: 'Connectez-vous ou créez un compte pour suivre vos inscriptions MTC.',
  });

  if (user && mode !== 'reset') {
    navigate(redirectTo, { replace: true });
    return null;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!email.trim()) {
      setError('Email requis');
      return;
    }
    if (mode !== 'reset' && password.length < 8) {
      setError('Mot de passe : 8 caractères minimum');
      return;
    }
    if (mode === 'signup') {
      if (password !== confirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        return;
      }
      if (!firstName.trim() || !lastName.trim()) {
        setError('Prénom et nom requis');
        return;
      }
    }
    setBusy(true);
    try {
      if (mode === 'signin') {
        await signIn(email, password);
        // Redirect happens via the `if (user && ...)` branch above on next render.
      } else if (mode === 'signup') {
        await signUp(email, password, `${firstName.trim()} ${lastName.trim()}`);
        navigate('/mon-espace', { replace: true });
      } else {
        await resetPassword(email);
        setInfo(
          'Un email de réinitialisation vient de partir. Suivez le lien pour choisir un nouveau mot de passe.',
        );
      }
    } catch (err) {
      setError(humaniseFirebaseError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        maxWidth: 460,
        margin: '64px auto 96px',
        padding: '0 24px',
      }}
    >
      <button
        type="button"
        className="mtc-back"
        onClick={() => navigate('/')}
        style={{ marginBottom: 16 }}
      >
        ← Accueil
      </button>

      <header style={{ marginBottom: 32 }}>
        <span className="mtc-eyebrow">Espace membre</span>
        <h1
          style={{
            fontFamily: 'var(--mtc-font-display)',
            fontSize: 'clamp(32px, 4vw, 48px)',
            fontWeight: 400,
            lineHeight: 1.05,
            letterSpacing: '-0.03em',
            margin: '8px 0 8px',
          }}
        >
          {mode === 'signin' && 'Connexion'}
          {mode === 'signup' && 'Créer un compte'}
          {mode === 'reset' && 'Mot de passe oublié'}
        </h1>
        <p style={{ color: 'rgba(15,25,22,0.65)', fontSize: 14, lineHeight: 1.5, margin: 0 }}>
          {mode === 'signin' &&
            'Accédez à vos inscriptions, votre suivi de paiement et bientôt vos certificats.'}
          {mode === 'signup' &&
            'Créez un compte pour ne plus ressaisir vos infos et suivre vos formations en ligne.'}
          {mode === 'reset' &&
            'Saisissez votre email — nous vous enverrons un lien de réinitialisation.'}
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        {mode === 'signup' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Prénom" value={firstName} onChange={setFirstName} />
            <Field label="Nom" value={lastName} onChange={setLastName} />
          </div>
        )}
        <Field label="Email" type="email" value={email} onChange={setEmail} />
        {mode !== 'reset' && (
          <Field
            label="Mot de passe"
            type="password"
            value={password}
            onChange={setPassword}
          />
        )}
        {mode === 'signup' && (
          <Field
            label="Confirmer le mot de passe"
            type="password"
            value={confirmPassword}
            onChange={setConfirmPassword}
          />
        )}

        {error && (
          <p
            role="alert"
            style={{
              fontSize: 13,
              color: '#9B1C2C',
              background: 'rgba(200,16,46,0.08)',
              border: '1px solid rgba(200,16,46,0.18)',
              borderRadius: 8,
              padding: '8px 12px',
              margin: 0,
            }}
          >
            {error}
          </p>
        )}
        {info && (
          <p
            style={{
              fontSize: 13,
              color: '#0B5D3B',
              background: 'rgba(11,93,59,0.08)',
              border: '1px solid rgba(11,93,59,0.18)',
              borderRadius: 8,
              padding: '8px 12px',
              margin: 0,
            }}
          >
            {info}
          </p>
        )}

        <button
          type="submit"
          className="mtc-btn mtc-btn-primary"
          disabled={busy}
          style={{ marginTop: 8 }}
        >
          {busy
            ? 'Patientez…'
            : mode === 'signin'
              ? 'Se connecter'
              : mode === 'signup'
                ? 'Créer mon compte'
                : 'Envoyer le lien'}
        </button>
      </form>

      <div style={{ marginTop: 28, fontSize: 13, color: 'rgba(15,25,22,0.65)', lineHeight: 1.6 }}>
        {mode === 'signin' && (
          <>
            <div>
              Pas encore de compte ?{' '}
              <button type="button" className="mtc-btn mtc-btn-link" onClick={() => setMode('signup')}>
                Créer un compte
              </button>
            </div>
            <div>
              Mot de passe oublié ?{' '}
              <button type="button" className="mtc-btn mtc-btn-link" onClick={() => setMode('reset')}>
                Réinitialiser
              </button>
            </div>
          </>
        )}
        {mode === 'signup' && (
          <div>
            Déjà un compte ?{' '}
            <button type="button" className="mtc-btn mtc-btn-link" onClick={() => setMode('signin')}>
              Se connecter
            </button>
          </div>
        )}
        {mode === 'reset' && (
          <div>
            <button type="button" className="mtc-btn mtc-btn-link" onClick={() => setMode('signin')}>
              ← Retour à la connexion
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function humaniseFirebaseError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes('auth/email-already-in-use')) {
    return 'Cet email a déjà un compte. Essayez de vous connecter.';
  }
  if (msg.includes('auth/invalid-credential') || msg.includes('auth/wrong-password')) {
    return 'Email ou mot de passe incorrect.';
  }
  if (msg.includes('auth/user-not-found')) {
    return 'Aucun compte trouvé avec cet email.';
  }
  if (msg.includes('auth/invalid-email')) {
    return 'Email invalide.';
  }
  if (msg.includes('auth/weak-password')) {
    return 'Mot de passe trop faible — 8 caractères minimum.';
  }
  if (msg.includes('auth/too-many-requests')) {
    return 'Trop de tentatives. Réessayez dans quelques minutes.';
  }
  return msg.replace(/^Firebase: /, '');
}
